import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { ChevronDownIcon, PencilIcon } from 'lucide-react';

interface InlineEditNumberProps {
  type: 'number';
  value: number;
  onSave: (value: number) => Promise<void>;
  label: string;
  suffix?: string;
  min?: number;
  max?: number;
  displayValue?: string;
}

interface InlineEditTextProps {
  type: 'text';
  value: string;
  onSave: (value: string) => Promise<void>;
  label: string;
  multiline?: boolean;
  rows?: number;
  mono?: boolean;
}

interface InlineEditSelectProps {
  type: 'select';
  value: string;
  onSave: (value: string) => Promise<void>;
  label: string;
  options: { value: string; label: string }[];
}

/** Renders the value as plain text, with no way into edit mode. */
type InlineEditProps = (InlineEditNumberProps | InlineEditTextProps | InlineEditSelectProps) & { readOnly?: boolean };

function validateNumber(draft: string, num: number, min?: number, max?: number): string | null {
  // A number input reports non-numeric text like "abc" as an empty string
  if (draft.trim() === '' || !Number.isInteger(num)) return 'Enter a whole number';
  const outOfRange = (min !== undefined && num < min) || (max !== undefined && num > max);
  if (!outOfRange) return null;
  if (min !== undefined && max !== undefined) return `Must be between ${min} and ${max}`;
  return min !== undefined ? `Must be at least ${min}` : `Must be at most ${max}`;
}

export function InlineEdit(props: InlineEditProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  const startEditing = useCallback(() => {
    setDraft(String(props.value));
    setIsEditing(true);
  }, [props.value]);

  const cancel = useCallback(() => {
    setIsEditing(false);
    setDraft('');
    setValidationError(null);
  }, []);

  const save = useCallback(async () => {
    if (saving) return;

    // Don't save if value hasn't changed
    if (draft === String(props.value)) {
      cancel();
      return;
    }

    // Invalid input keeps the editor open with a message, so it's never mistaken for a save
    let num: number | undefined;
    if (props.type === 'number') {
      num = Number(draft);
      const error = validateNumber(draft, num, props.min, props.max);
      if (error) {
        setValidationError(error);
        return;
      }
    }

    setSaving(true);
    try {
      if (props.type === 'number') {
        await props.onSave(num as number);
      } else {
        await props.onSave(draft);
      }
      setIsEditing(false);
      setValidationError(null);
    } catch {
      // Toast will handle error display
    } finally {
      setSaving(false);
    }
  }, [draft, props, saving, cancel]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !(props.type === 'text' && 'multiline' in props)) {
      e.preventDefault();
      save();
    }
    if (e.key === 'Escape') {
      cancel();
    }
  }, [save, cancel, props]);

  const isMultiline = props.type === 'text' && 'multiline' in props && props.multiline;

  if (isEditing) {
    return (
      <div className={isMultiline ? 'w-full' : 'inline-flex items-center gap-1'}>
        {props.type === 'select' ? (
          <select
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); }}
            onBlur={save}
            onKeyDown={handleKeyDown}
            disabled={saving}
            className="select select-xs text-sm"
          >
            {props.options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : props.type === 'text' && 'multiline' in props && props.multiline ? (
          <div className="flex flex-col gap-1 w-full">
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={saving}
              rows={props.rows ?? 3}
              className={`textarea textarea-xs text-sm w-full ${props.mono ? 'font-mono' : ''}`}
            />
            <div className="flex gap-1 justify-end">
              <button type="button" onClick={cancel} className="btn btn-xs btn-ghost" disabled={saving}>Cancel</button>
              <button type="button" onClick={save} className="btn btn-xs btn-primary" disabled={saving}>
                {saving ? '...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className="inline-flex flex-col gap-0.5">
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={props.type === 'number' ? 'number' : 'text'}
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setValidationError(null); }}
              onBlur={save}
              onKeyDown={handleKeyDown}
              disabled={saving}
              min={props.type === 'number' ? props.min : undefined}
              max={props.type === 'number' ? props.max : undefined}
              aria-label={props.label}
              aria-invalid={validationError ? true : undefined}
              aria-describedby={validationError ? errorId : undefined}
              className={`input input-xs w-20 text-sm ${validationError ? 'input-error' : ''} ${props.type === 'text' && 'mono' in props && props.mono ? 'font-mono' : ''}`}
            />
            {validationError && (
              <p id={errorId} role="alert" className="text-xs text-error">{validationError}</p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Display mode
  const displayText = props.type === 'number' && 'displayValue' in props && props.displayValue
    ? props.displayValue
    : props.type === 'select'
      ? props.options.find(o => o.value === props.value)?.label ?? props.value
      : props.type === 'number' && 'suffix' in props && props.suffix
        ? `${props.value}${props.suffix}`
        : String(props.value);

  if (props.readOnly) {
    return (
      <span className={`text-sm font-medium ${props.type === 'text' && 'mono' in props && props.mono ? 'font-mono' : ''}`}>
        {displayText}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={startEditing}
      className="group inline-flex items-center gap-1.5 text-sm font-medium hover:text-primary transition-colors cursor-pointer rounded-field px-1 -mx-1 hover:bg-base-200"
      title={`Click to edit ${props.label.toLowerCase()}`}
    >
      <span className={`border-b border-dashed border-base-content/25 group-hover:border-primary/50 transition-colors ${props.type === 'text' && 'mono' in props && props.mono ? 'font-mono' : ''}`}>
        {displayText}
      </span>
      <span className="opacity-30 group-hover:opacity-60 transition-opacity">
        {props.type === 'select' ? <ChevronDownIcon className="size-3.5" /> : <PencilIcon className="size-3.5" />}
      </span>
    </button>
  );
}
